/**
 * PWA plumbing — service-worker registration, install-prompt capture, and the
 * platform detection behind the mobile install banner.
 *
 * This is a side-effecting module imported from `main.tsx`, not a hook,
 * because `beforeinstallprompt` fires once and early: Chrome dispatches it as
 * soon as the page qualifies, which can be before React has mounted. An event
 * nobody listened for is gone for good, taking the one-tap install with it.
 * So the listener is installed at import time and the event is parked in
 * module scope until a component asks for it.
 *
 * State is exposed as an external store (`subscribe` / `getInstallState`) so
 * `InstallBanner` can read it through `useSyncExternalStore` — the parked
 * event lives outside React's tree by necessity.
 */

import { isNativeHost } from "@/api/client";

/** Where "get the native app" sends people. Campaign-tagged so the downloads
 *  page can attribute installs that started in the web app. */
export const NATIVE_DOWNLOADS_URL =
  "https://nbyula-releases.s3.us-east-1.amazonaws.com/cowork/downloads.html" +
  "?utm_source=omniwerk.nbyula.com&utm_medium=referral&utm_campaign=onboarding";

/** Chromium's install prompt. Not in lib.dom — declared here, minimally. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Mobile OS families we tailor the banner for. `null` = desktop or unknown,
 *  where the banner never shows. */
export type MobilePlatform = "android" | "ios";

/** How long a dismissal sticks. Long enough not to nag, short enough that
 *  someone who said "not now" in their first week gets asked again. */
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

const SNOOZE_KEY = "cowork-install-snoozed-until";
/** Sentinel written instead of a timestamp once the app is actually
 *  installed — a value that never expires. */
const SNOOZE_FOREVER = "installed";

export interface InstallState {
  /** A deferred prompt is parked: the banner can offer one-tap install. */
  canPrompt: boolean;
  /** Already running as an installed app — nothing left to offer. */
  standalone: boolean;
  /** The browser reported a completed install during this session. */
  installed: boolean;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

// `useSyncExternalStore` compares snapshots by identity, so this object is
// replaced only when something actually changes — returning a fresh object on
// every read would spin React into an infinite re-render loop.
let snapshot: InstallState = {
  canPrompt: false,
  standalone: detectStandalone(),
  installed: false,
};

const listeners = new Set<() => void>();

function emit(next: Partial<InstallState>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((fn) => fn());
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getInstallState(): InstallState {
  return snapshot;
}

/** True when the page is running from the home screen rather than a browser
 *  tab. `display-mode` covers Android/Chromium and modern iOS; the legacy
 *  `navigator.standalone` covers older iOS Safari, which never shipped it. */
function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const modes = ["standalone", "minimal-ui", "fullscreen"];
  const byDisplayMode = modes.some(
    (m) => window.matchMedia?.(`(display-mode: ${m})`).matches,
  );
  const iosLegacy = !!(navigator as unknown as { standalone?: boolean }).standalone;
  return byDisplayMode || iosLegacy;
}

/**
 * Which mobile OS this is, or `null` for desktop.
 *
 * UA sniffing is the only option here — there is no feature test for "this
 * device has a home screen", and the install gesture differs per platform, so
 * we have to name the platform to describe it. iPadOS 13+ deliberately
 * masquerades as desktop Safari, hence the touch-points check: `MacIntel`
 * with a touchscreen is an iPad, since no Mac reports multi-touch.
 */
export function mobilePlatform(): MobilePlatform | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/i.test(ua)) return "ios";
  const nav = navigator as Navigator & { platform?: string };
  if (nav.platform === "MacIntel" && nav.maxTouchPoints > 1) return "ios";
  return null;
}

/** Whether the banner is currently snoozed (or permanently retired because
 *  the app got installed). Reads defensively — Safari private mode throws on
 *  `localStorage` access. */
export function installBannerSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return false;
    if (raw === SNOOZE_FOREVER) return true;
    return Date.now() < Number(raw);
  } catch {
    return false;
  }
}

/** "Not now" — hide the banner for {@link SNOOZE_MS}. */
export function snoozeInstallBanner(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch {
    /* storage unavailable — the banner just reappears next session */
  }
}

/** Retire the banner for good (the app is installed). */
function retireInstallBanner(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, SNOOZE_FOREVER);
  } catch {
    /* see above */
  }
}

/**
 * Fire the parked prompt. Resolves to the user's choice, or `"unavailable"`
 * when there was no prompt to show (iOS, or a Chrome that never offered one).
 *
 * The event is single-use: once consumed Chrome will not hand it back, so it
 * is cleared either way and a declined install falls through to the snooze.
 */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const evt = deferredPrompt;
  if (!evt) return "unavailable";
  deferredPrompt = null;
  emit({ canPrompt: false });
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    return outcome;
  } catch {
    return "unavailable";
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppress Chrome's own mini-infobar so it can't double up with our banner.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit({ canPrompt: true });
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    retireInstallBanner();
    emit({ canPrompt: false, installed: true });
  });

  // A tab can transition into standalone (the user installs, then the same tab
  // is adopted by the app window), so track it rather than reading once.
  window
    .matchMedia?.("(display-mode: standalone)")
    .addEventListener?.("change", () => emit({ standalone: detectStandalone() }));
}

/**
 * Register the pass-through worker that keeps this app installable.
 *
 * Skipped in the native pywebview shell, where there is nothing to install and
 * the embedded webview gains nothing from a worker. Deferred to `load` so it
 * never competes with the first paint, and failures are swallowed — a browser
 * that refuses the registration (private mode, insecure context, disabled
 * workers) should lose the install banner, not the app. See `public/sw.js` for
 * why it caches nothing.
 */
export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (isNativeHost() || !("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      /* not installable here — banner degrades to manual instructions */
    });
  });
}
